/* eslint-disable no-nested-ternary */
/* eslint-disable camelcase */
import {
  Button,
  ButtonEmpty,
  ButtonIcon,
  FlexGroup,
  FlexItem,
  Flyout,
  FlyoutBody,
  FlyoutFooter,
  FlyoutHeader,
  Form,
  FormRow,
  Icon,
  notification,
  Spacer,
  Text,
  TextArea,
  Title,
  ToolTip,
} from '@antoree/ant-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useToggle } from 'hooks';
import Rating from 'react-rating';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Session,
  useCreateEValuation,
  useRetrieveSessionEvaluation,
} from 'services';
import * as yup from 'yup';
import { RATINGS, STARS } from './constants';
import styles from './PeriodicalEvaluation.module.scss';

interface IFormData {
  strong_points: string;
  weak_points: string;
  suggestion: string;
}
export type PeriodicalEvaluationProps = {
  session: Session;
  view: boolean;
  allowSubmit: boolean;
  onReload: (session: Session) => void;
};
export const CONFIRM_SESSION_FORM_ID = 'CONFIRM_SESSION_FORM_ID';

const PeriodicalEvaluation: React.FC<PeriodicalEvaluationProps> = ({
  session,
  view,
  allowSubmit,
  onReload,
}) => {
  const [ratings, setRatings] = useState<any>(RATINGS);
  const { isVisiable, toggle, close } = useToggle();
  const intl = useIntl();

  // Helper function to get translated rating names with static IDs
  const getRatingName = (nameKey: string) => {
    switch (nameKey) {
      case 'periodical_evaluation.listen':
        return intl.formatMessage({
          id: 'periodical_evaluation.listen',
          defaultMessage: 'Listen',
        });
      case 'periodical_evaluation.speak':
        return intl.formatMessage({
          id: 'periodical_evaluation.speak',
          defaultMessage: 'Speak',
        });
      case 'periodical_evaluation.read':
        return intl.formatMessage({
          id: 'periodical_evaluation.read',
          defaultMessage: 'Read',
        });
      case 'periodical_evaluation.write':
        return intl.formatMessage({
          id: 'periodical_evaluation.write',
          defaultMessage: 'Write',
        });
      case 'periodical_evaluation.listen.grammar':
        return intl.formatMessage({
          id: 'periodical_evaluation.listen.grammar',
          defaultMessage: 'Avoid grammatical mistakes',
        });
      case 'periodical_evaluation.listen.receptive':
        return intl.formatMessage({
          id: 'periodical_evaluation.listen.receptive',
          defaultMessage: 'Second Language Acquisition',
        });
      case 'periodical_evaluation.listen.attitude':
        return intl.formatMessage({
          id: 'periodical_evaluation.listen.attitude',
          defaultMessage: 'Attitude towards English learning',
        });
      case 'periodical_evaluation.write.phrasal_verbs':
        return intl.formatMessage({
          id: 'periodical_evaluation.write.phrasal_verbs',
          defaultMessage: 'Use phrasal verbs and collocations',
        });
      case 'periodical_evaluation.write.vocabulary':
        return intl.formatMessage({
          id: 'periodical_evaluation.write.vocabulary',
          defaultMessage: 'Use a wide range of vocabulary',
        });
      case 'periodical_evaluation.write.grammar_variety':
        return intl.formatMessage({
          id: 'periodical_evaluation.write.grammar_variety',
          defaultMessage: 'Use a variety of grammatical forms',
        });
      case 'periodical_evaluation.speak.avoid_silence':
        return intl.formatMessage({
          id: 'periodical_evaluation.speak.avoid_silence',
          defaultMessage: 'Avoid silence or hesitation',
        });
      case 'periodical_evaluation.speak.talk_long':
        return intl.formatMessage({
          id: 'periodical_evaluation.speak.talk_long',
          defaultMessage: 'Speak at length on each topic',
        });
      case 'periodical_evaluation.speak.pronunciation':
        return intl.formatMessage({
          id: 'periodical_evaluation.speak.pronunciation',
          defaultMessage: 'Pronounce accurately',
        });
      case 'periodical_evaluation.read.avoid_mistakes':
        return intl.formatMessage({
          id: 'periodical_evaluation.read.avoid_mistakes',
          defaultMessage: 'Avoid grammatical mistakes',
        });
      case 'periodical_evaluation.read.receptive':
        return intl.formatMessage({
          id: 'periodical_evaluation.read.receptive',
          defaultMessage: 'Second Language Acquisition',
        });
      case 'periodical_evaluation.read.attitude':
        return intl.formatMessage({
          id: 'periodical_evaluation.read.attitude',
          defaultMessage: 'Attitude towards English learning',
        });
      case 'periodical_evaluation.rating.need_more_effort':
        return intl.formatMessage({
          id: 'periodical_evaluation.rating.need_more_effort',
          defaultMessage: 'Need more effort',
        });
      case 'periodical_evaluation.rating.need_improvement':
        return intl.formatMessage({
          id: 'periodical_evaluation.rating.need_improvement',
          defaultMessage: 'Need more improvement',
        });
      case 'periodical_evaluation.rating.average':
        return intl.formatMessage({
          id: 'periodical_evaluation.rating.average',
          defaultMessage: 'Average',
        });
      case 'periodical_evaluation.rating.good':
        return intl.formatMessage({
          id: 'periodical_evaluation.rating.good',
          defaultMessage: 'Good',
        });
      case 'periodical_evaluation.rating.excellent':
        return intl.formatMessage({
          id: 'periodical_evaluation.rating.excellent',
          defaultMessage: 'Excellent',
        });
      default:
        return nameKey;
    }
  };
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = useForm<IFormData>({
    mode: 'all',
    defaultValues: {
      strong_points: '',
      weak_points: '',
      suggestion: '',
    },
    resolver: yupResolver(
      yup.object().shape({
        strong_points: yup
          .string()
          .required('This field  is a required field')
          .min(150, 'This field must be at least 150 characters'),
        weak_points: yup
          .string()
          .required('This field  is a required field')
          .min(150, 'This field must be at least 150 characters'),
        suggestion: yup
          .string()
          .required('This field  is a required field')
          .min(150, 'This field must be at least 150 characters'),
      }),
    ),
  });
  const { data } = useRetrieveSessionEvaluation(
    { id: session.id, isCourseUnit: session.isCourseUnit ? 1 : 0 },
    {
      enabled: isVisiable,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  );
  useEffect(() => {
    reset({
      strong_points: data?.data?.evaluation?.ratings?.strong_points,
      weak_points: data?.data?.evaluation?.ratings?.weak_points,
      suggestion: data?.data?.evaluation?.ratings?.suggestion,
    });
  }, [data]);

  const { mutate, isLoading } = useCreateEValuation({
    onSuccess: response => {
      onReload(response?.data?.session);
      notification.success({
        title: (
          <FormattedMessage defaultMessage="Add session evaluation success" />
        ),
      });
      close();
    },
    onError: err => {
      console.log(err);

      const mesError = err?.response?.data?.errors[0]?.message;

      notification.error({
        title: (
          <FormattedMessage defaultMessage="Add session evaluation failed" />
        ),
        text: `${mesError}`,
      });
    },
  });

  const handleClickStar = (number: number, id: number, ratingId: number) => {
    setRatings((prev: any) =>
      prev.map((item: any) => {
        if (item.id === ratingId) {
          return {
            ...item,
            value: item.value.map((val: any) => {
              if (val.id === id) {
                return { ...val, star: number };
              }
              return { ...val };
            }),
          };
        }
        return { ...item };
      }),
    );
  };
  const onSubmit = (formData: IFormData) => {
    const { strong_points, suggestion, weak_points } = formData;
    const strongPointReplaced = strong_points.replace(/[^a-zA-Z0-9]/g, '');
    const suggestionReplaced = suggestion.replace(/[^a-zA-Z0-9]/g, '');
    const weakPointsReplaced = weak_points.replace(/[^a-zA-Z0-9]/g, '');
    if (strongPointReplaced === suggestionReplaced) {
      notification.error({
        title: (
          <FormattedMessage defaultMessage="Strong Point field and Suggestion field have the same value " />
        ),
      });
      return;
    }
    if (strongPointReplaced === weakPointsReplaced) {
      notification.error({
        title: (
          <FormattedMessage defaultMessage="Strong Point field and Need to improve field have the same value " />
        ),
      });
      return;
    }
    if (suggestionReplaced === weakPointsReplaced) {
      notification.error({
        title: (
          <FormattedMessage defaultMessage="Need to improve field and Suggestion field have the same value " />
        ),
      });
      return;
    }

    mutate({
      id: session.id,
      courseId: session.course_id,
      ...formData,
      isCourseUnit: session?.isCourseUnit ? 1 : 0,
      ratings,
    });
  };
  useEffect(() => {
    if (data?.data?.evaluation?.ratings?.listen?.pronunciation) {
      const {
        strong_points,
        suggestion,
        weak_points,
        ...obj
      } = data?.data?.evaluation?.ratings;
      const dataFormatted = Object.entries(obj).map(item => {
        const { detail, checked, ...value } = item[1];
        return {
          dataName: item[0],
          value: Object.entries(value),
        };
      });

      setRatings((prev: any) =>
        // eslint-disable-next-line array-callback-return, consistent-return
        prev.map((ra: any) => {
          if (dataFormatted.map(item => item.dataName).includes(ra.dataName)) {
            return {
              ...ra,
              value: ra.value.map((val: any) => {
                let a = {};
                dataFormatted.forEach(dataX => {
                  dataX.value.forEach(dataY => {
                    if (
                      dataY[0] === val.value &&
                      dataX.dataName === ra.dataName
                    ) {
                      a = { ...val, star: dataY[1] };
                    }
                  });
                });
                return { ...a };
              }),
            };
          }
        }),
      );
    }
  }, [data]);

  return (
    <>
      {session.evaluation && session.hasEvaluation && (
        <ToolTip position="top" content="View Evaluation">
          <ButtonIcon
            color="text"
            iconType="visBarVertical"
            aria-label="Evaluation"
            onClick={toggle}
          />
        </ToolTip>
      )}
      {!session.evaluation && session.hasEvaluation && (
        <ToolTip position="top" content="Add Evaluation">
          <div className={styles.evaluationIcon}>
            <ButtonIcon
              iconType="visBarVertical"
              aria-label="Evaluation"
              onClick={toggle}
              style={{ backgroundColor: 'rgba(0, 192, 129, 0.15)' }}
            />
            <span className="text-danger">*</span>
          </div>
        </ToolTip>
      )}

      {isVisiable && (
        <Flyout
          style={{ width: '70vw' }}
          ownFocus
          outsideClickCloses={false}
          onClose={() => close()}
        >
          <FlyoutHeader>
            <Title size="m">
              <h2>
                <FormattedMessage
                  id="periodical_evaluation.title"
                  defaultMessage="Add Periodical Evaluation"
                />
              </h2>
            </Title>
            <Text color="subdued" size="s">
              <FormattedMessage
                id="periodical_evaluation.note"
                defaultMessage="Note: After submission, an email will be automatically sent to the student and cannot be edited."
              />
            </Text>
          </FlyoutHeader>
          <FlyoutBody>
            <Form
              id={CONFIRM_SESSION_FORM_ID}
              component="form"
              onSubmit={handleSubmit(dataForm => onSubmit(dataForm))}
            >
              <div className="grid grid-cols-2  gap-4 my-2">
                {ratings.map((rating: any) => (
                  <div>
                    <div
                      className=" text-white w-28 text-center py-1 rounded"
                      style={{ background: '#FFC700' }}
                    >
                      <Text className="font-medium text-xl">
                        {getRatingName(rating.nameKey)}
                      </Text>
                    </div>

                    {rating.value.map((value: any) => (
                      <div>
                        <Text
                          className="font-medium text-lg"
                          // style={{ color: '#00FF47' }}
                        >
                          {getRatingName(value.nameKey)}
                        </Text>
                        <div className="flex items-center gap-1">
                          <div>
                            <Rating
                              emptySymbol={
                                <Icon
                                  type="starEmptySpace"
                                  size="l"
                                  color="#FFC700"
                                />
                              }
                              fullSymbol={
                                <Icon
                                  type="starFilledSpace"
                                  color="#FFC700"
                                  size="l"
                                />
                              }
                              onClick={e =>
                                handleClickStar(e, value.id, rating.id)
                              }
                              initialRating={value.star}
                            />
                          </div>

                          <div
                            style={{
                              color: STARS.find(item => item.id === value.star)
                                ?.color,
                            }}
                            className="w-38 text-left "
                          >
                            {getRatingName(
                              STARS.find(item => item.id === value.star)
                                ?.rateKey || '',
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <FlexGroup>
                <FlexItem>
                  <FormRow
                    fullWidth
                    label={
                      <FormattedMessage
                        id="periodical_evaluation.strong_points"
                        defaultMessage="Strengths"
                      />
                    }
                    isInvalid={!!errors.strong_points}
                    error={errors.strong_points?.message}
                    required
                  >
                    <Controller
                      name="strong_points"
                      control={control}
                      render={({ field: { ref, ...fieldRest } }) => (
                        <TextArea
                          className="rounded-lg"
                          rows={2}
                          inputRef={ref}
                          {...fieldRest}
                          isInvalid={!!errors.strong_points}
                          // minLength={150}

                          fullWidth
                          placeholder={intl.formatMessage({
                            id: 'periodical_evaluation.strong_points',
                            defaultMessage: 'Strengths',
                          })}
                          style={{ height: '120px' }}
                          disabled={view}
                        />
                      )}
                    />
                  </FormRow>
                </FlexItem>
              </FlexGroup>

              <FlexGroup>
                <FlexItem>
                  <FormRow
                    required
                    fullWidth
                    isInvalid={!!errors.weak_points}
                    error={errors.weak_points?.message}
                    label={
                      <FormattedMessage
                        id="periodical_evaluation.weak_points"
                        defaultMessage="Areas for Improvement"
                      />
                    }
                  >
                    <Controller
                      name="weak_points"
                      control={control}
                      render={({ field: { ref, value, ...fieldRest } }) => (
                        <TextArea
                          rows={2}
                          placeholder={intl.formatMessage({
                            id: 'periodical_evaluation.weak_points_placeholder',
                            defaultMessage: 'Weaknesses',
                          })}
                          // minLength={150}
                          isInvalid={!!errors.strong_points}
                          fullWidth
                          inputRef={ref}
                          {...fieldRest}
                          style={{ height: '120px' }}
                          disabled={view}
                        />
                      )}
                    />
                  </FormRow>
                </FlexItem>
              </FlexGroup>

              <FlexGroup>
                <FlexItem>
                  <FormRow
                    required
                    fullWidth
                    label={
                      <FormattedMessage
                        id="periodical_evaluation.suggestion"
                        defaultMessage="Suggestions"
                      />
                    }
                    isInvalid={!!errors.suggestion}
                    error={errors.suggestion?.message}
                  >
                    <Controller
                      name="suggestion"
                      control={control}
                      render={({ field: { ref, value, ...fieldRest } }) => (
                        <TextArea
                          rows={2}
                          placeholder={intl.formatMessage({
                            id: 'periodical_evaluation.suggestion_placeholder',
                            defaultMessage: 'Notes for student',
                          })}
                          isInvalid={!!errors.strong_points}
                          inputRef={ref}
                          // minLength={150}
                          {...fieldRest}
                          fullWidth
                          style={{ height: '120px' }}
                          disabled={view}
                        />
                      )}
                    />
                  </FormRow>
                </FlexItem>
              </FlexGroup>
              <Spacer />
            </Form>
          </FlyoutBody>
          <FlyoutFooter style={{ backgroundColor: '#fff' }}>
            {allowSubmit ? (
              <>
                <FlexGroup justifyContent="spaceBetween">
                  <FlexItem grow={false}>
                    <ButtonEmpty onClick={() => close()}>
                      <FormattedMessage
                        id="periodical_evaluation.cancel"
                        defaultMessage="Cancel"
                      />
                    </ButtonEmpty>
                  </FlexItem>
                  <FlexItem grow={false}>
                    <Button
                      fill
                      type="submit"
                      form={CONFIRM_SESSION_FORM_ID}
                      isLoading={isLoading || false}
                    >
                      <FormattedMessage
                        id="periodical_evaluation.confirm"
                        defaultMessage="Confirm"
                      />
                    </Button>
                  </FlexItem>
                </FlexGroup>
              </>
            ) : (
              <>
                <FlexGroup justifyContent="flexStart">
                  <FlexItem grow={false}>
                    <ButtonEmpty onClick={() => close()}>
                      <FormattedMessage
                        id="periodical_evaluation.close"
                        defaultMessage="Close"
                      />
                    </ButtonEmpty>
                  </FlexItem>
                </FlexGroup>
              </>
            )}
          </FlyoutFooter>
        </Flyout>
      )}
    </>
  );
};

export default PeriodicalEvaluation;
